import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Table } from 'ksnm-common-ui/lib/Table2';
import type { LocalDataSource } from 'ksnm-common-ui/lib/Table2/contexts/Table/TableContext.interface';

import { DesignLayout } from '@common/DesignLayout';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { constantStore } from '@stores/constantStore';
import { userRoleReportStore } from '@stores/userRoleReportStore';

import styles from './UserRoleReport.module.scss';

const { TABLE_NAMES } = constantStore;

export const UserRoleReport = observer(() => {
  const { getReportUserAudit, userAudit, getExcel } = userRoleReportStore;

  const { users } = userAudit;

  const [key, update] = useUpdateTable({ tableName: TABLE_NAMES.USER_ROLE_REPORT });

  useEffect(() => update(), [update, users]);

  useEffect(() => {
    getReportUserAudit();
  }, [getReportUserAudit]);

  const columns = useMemo(
    () => [
      {
        title: 'Идентификатор пользователя',
        key: 'id',
        width: 310,
      },
      {
        title: 'Логин пользователя',
        key: 'login',
        width: 200,
      },
      {
        title: 'Имя пользователя',
        key: 'first_name',
        width: 200,
      },
      {
        title: 'Фамилия пользователя',
        key: 'last_name',
        width: 200,
      },
      {
        title: 'Фильтрация по организации',
        key: 'organization_filter',
        width: 500,
        render: ({ value }) => <div className={styles.cell}>{value?.split(',').join(', ')}</div>,
      },
      {
        title: 'Фильтрация по группе отчётов',
        key: 'group_filter',
        width: 500,
        render: ({ value }) => <div className={styles.cell}>{value?.split(',').join(', ')}</div>,
      },
      {
        title: 'Дата регистрации',
        key: 'created_timestamp',
        width: 200,
      },
      {
        title: 'Статус',
        key: 'enabled',
        width: 150,
      },
      {
        title: 'Роли',
        key: 'roles',
        width: 500,
        render: ({ value }) => <div className={styles.cell}>{value}</div>,
      },
    ],
    [],
  );

  const localData = useMemo<LocalDataSource>(
    () => ({
      data: users
        ? users.map((item) => ({
            ...item.fields,
            enabled: item.fields?.enabled ? 'Активна' : 'Заблокирована',
            roles: (item.fields?.roles || []).join(', '),
          }))
        : [],
      fieldFormat: {
        id: 'string',
        login: 'string',
        first_name: 'string',
        last_name: 'string',
        organization_filter: 'string',
        group_filter: 'string',
        enabled: 'string',
        created_timestamp: 'timestamp',
        roles: 'string',
      },
    }),
    [users],
  );

  const extendTableButtons = useMemo(
    () => (
      <>
        <IconButton key="download" iconName="download" title="Скачать" onClick={getExcel} />
      </>
    ),
    [getExcel],
  );

  return (
    <DesignLayout scrollEnable>
      <Table
        localDataSource={localData}
        columns={columns}
        key={key}
        tableTitle="Отчет по ролям пользователей"
        tableName={TABLE_NAMES.USER_ROLE_REPORT}
        resource=""
        showAggregateRow={false}
        cellHeight={48}
        extendTableButtons={extendTableButtons}
      />
    </DesignLayout>
  );
});
